import React, { ReactNode } from "react"
import { View } from "react-native"

import styled from "styled-components/native"

import { AppLink as AppLinkType, MediaImageFile } from "@treefort/api-spec"
import { getOptimizedImageSource } from "@treefort/lib/get-optimized-image-source"
import rawTokens from "@treefort/tokens/app"

import { IS_BOT } from "../lib/is-bot"
import { kebabCase } from "../lib/kebab-case"
import { AppLink } from "./app-link"
import { ImageWithFade, ImageWithFadeProps } from "./image-with-fade"
import LockedContentIndicator from "./locked-content-indicator"
import Text from "./text"
import TextPlaceholder from "./text-placeholder"
import { ResolvedTokens } from "./tokens-provider"

interface ArtworkListItemProps {
  data: ItemData
  artworkShape: ArtworkShape
  // If dimensions are defined, artworkShape will still be
  // used for border radius
  artworkDimensions?: Dimensions
  titleNumberOfLines?: number
  subtitleNumberOfLines?: number
  withoutBottomSpacing?: boolean
  withoutRightSpacing?: boolean
  height?: number
  progressBar?: React.JSX.Element
}

export type ArtworkDisplayMode =
  | { mode: "cover" }
  | { mode: "containWithTransparentBackground" }
  | {
      mode: "containWithBlurhash"
      blurhash: string
      width: number
      height: number
    }

type ItemData =
  | {
      isLoading: false
      artwork?: string
      /**
       * Defaults to "cover"
       */
      artworkDisplayMode?: ArtworkDisplayMode
      link?: AppLinkType
      title: string
      subtitle?: string
      showLockIcon?: boolean
      linkParams?: Record<string, string>
    }
  | {
      isLoading: true
    }

export type ArtworkShape = keyof typeof rawTokens.artworkListItem.shape

type Dimensions = {
  width: number
  height: number
}

const BLURHASH_RESOLUTION = 2

export const TEXT_MARGIN_VERTICAL = rawTokens.spacing.tiny * 2

export const TEXT_GAP_VERTICAL = rawTokens.spacing.tiny

export const TEXT_STYLE_TITLE = "headingSmall"

export const TEXT_STYLE_SUBTITLE = "caption"

const ItemContainer = styled.View<{
  $withoutBottomSpacing?: boolean
  $withoutRightSpacing?: boolean
  $width?: number
  $height?: number
}>`
  ${(props) => (props.$width ? `width: ${props.$width}px;` : "flex: 1")};
  ${(props) => (props.$height ? `height: ${props.$height}px` : "")};
  margin-right: ${(props) =>
    props.$withoutRightSpacing ? 0 : props.theme.artworkListItem.spacing}px;
  margin-bottom: ${(props) =>
    props.$withoutBottomSpacing ? 0 : props.theme.artworkListItem.spacing}px;
`

const ArtworkContainer = styled(View)<{
  $shape: ArtworkShape
  $height?: number
  $transparentBackground?: boolean
}>`
  position: relative;
  overflow: hidden;
  background-color: ${(props) =>
    props.$transparentBackground
      ? "transparent"
      : props.theme.colors.loading.image};
  border-radius: ${(props) =>
    props.theme.artworkListItem.shape[props.$shape].borderRadius}px;
  width: 100%;
  ${(props) =>
    props.$height
      ? `height: ${props.$height}px`
      : `aspect-ratio: ${props.theme.artworkListItem.shape[props.$shape].aspectRatio}`};
  display: flex;
  align-items: center;
  justify-content: center;
`

const ArtworkImage = styled(ImageWithFade)<{
  $shape: ArtworkShape
  $artworkDisplayMode?: ArtworkDisplayMode
}>`
  ${(props) => {
    if (props.$artworkDisplayMode?.mode === "containWithBlurhash") {
      const artworkAspectRatio =
        props.$artworkDisplayMode.width / props.$artworkDisplayMode.height
      const shapeAspectRatio =
        props.theme.artworkListItem.shape[props.$shape].aspectRatio
      return `
        width: ${artworkAspectRatio >= shapeAspectRatio ? "90%" : undefined};
        height: ${artworkAspectRatio < shapeAspectRatio ? "90%" : undefined};
        aspect-ratio: ${artworkAspectRatio};
        border-radius: ${props.theme.artworkListItem.shape[props.$shape].borderRadius}px;
      `
    } else {
      return `
        width: 100%;
        height: 100%;
      `
    }
  }}
`

const ItemText = styled(Text)<{
  $marginTop: number
  $marginBottom: number
}>`
  margin-top: ${(props) => props.$marginTop}px;
  margin-bottom: ${(props) => props.$marginBottom}px;
  width: 100%;
  flex-direction: row;
`

const ItemTextPlaceholder = styled(TextPlaceholder)<{
  $alignment: "left" | "center"
  $width: string
  $marginTop: number
  $marginBottom: number
}>`
  margin-top: ${(props) => props.$marginTop}px;
  margin-bottom: ${(props) => props.$marginBottom}px;
  width: ${(props) => props.$width};
  ${(props) =>
    props.$alignment === "center"
      ? `margin-right: auto; margin-left: auto`
      : ""};
`

export function ItemArtwork({
  showLockIcon = false,
  artwork,
  artworkDisplayMode,
  progressBar = undefined,
  shape,
  height,
  ...imageProps
}: {
  height?: number
  shape: ArtworkShape
  showLockIcon?: boolean
  artwork?: string
  artworkDisplayMode?: ArtworkDisplayMode
  progressBar?: React.JSX.Element
} & Omit<ImageWithFadeProps, "source" | "resizeMode">): React.JSX.Element {
  return (
    <ArtworkContainer
      $shape={shape}
      $height={height}
      $transparentBackground={
        artworkDisplayMode?.mode === "containWithTransparentBackground"
      }
    >
      <ArtworkImage
        {...imageProps}
        blurhash={
          artworkDisplayMode?.mode === "containWithBlurhash"
            ? {
                hash: artworkDisplayMode.blurhash,
                resolutionX: BLURHASH_RESOLUTION,
                resolutionY: BLURHASH_RESOLUTION,
                width: "100%",
                height: "100%",
              }
            : undefined
        }
        $shape={shape}
        $artworkDisplayMode={artworkDisplayMode}
        resizeMode={
          artworkDisplayMode?.mode === "containWithTransparentBackground"
            ? "contain"
            : "cover"
        }
        source={
          // Don't load artwork for bots (waste of time and bandwidth)
          IS_BOT
            ? undefined
            : {
                uri: getOptimizedImageSource(
                  artwork,
                  // Use a fixed maximum size instead of the exact width/height props
                  // to increase the chances of a cache hit. If we used the exact
                  // width/height props then the same image would likely be requested
                  // several different times at different sizes. Using a fixed size
                  // (that is still in the ballpark of the sizes we are actually
                  // displaying) costs a little bit more bandwidth upfront but saves
                  // more in the long run as the user taps around and the same image
                  // is re-used.
                  rawTokens.artworkListItem.maxSourceSize,
                ),
              }
        }
      />
      {showLockIcon && shape !== "circle" ? <LockedContentIndicator /> : null}
      {progressBar}
    </ArtworkContainer>
  )
}

function ItemLink({
  data,
  children,
}: {
  data: ItemData
  children: ReactNode
}): React.JSX.Element {
  return !data.isLoading && data.link ? (
    <AppLink
      id={kebabCase(`item-link-${data.link.type}-${data.title}`)}
      to={data.link}
      params={data.linkParams}
      aria-label={data.title}
    >
      {children}
    </AppLink>
  ) : (
    <>{children}</>
  )
}

export default function ArtworkListItem({
  data,
  artworkDimensions,
  artworkShape,
  titleNumberOfLines,
  subtitleNumberOfLines,
  withoutBottomSpacing,
  withoutRightSpacing,
  height,
  progressBar,
}: ArtworkListItemProps): React.JSX.Element {
  const showTitle = Boolean(titleNumberOfLines)
  const showSubtitle = Boolean(subtitleNumberOfLines)

  return (
    <ItemContainer
      $withoutRightSpacing={withoutRightSpacing}
      $withoutBottomSpacing={withoutBottomSpacing}
      $width={artworkDimensions?.width}
      $height={height}
    >
      {data?.isLoading ? (
        <>
          <ArtworkContainer
            $height={artworkDimensions?.height}
            $shape={artworkShape}
          />
          {showTitle ? (
            <ItemTextPlaceholder
              $marginTop={TEXT_MARGIN_VERTICAL}
              $marginBottom={showSubtitle ? 0 : TEXT_MARGIN_VERTICAL}
              textStyle={TEXT_STYLE_TITLE}
              $alignment={artworkShape === "circle" ? "center" : "left"}
              $width="75%"
            />
          ) : null}
          {showSubtitle ? (
            <ItemTextPlaceholder
              $marginTop={showTitle ? TEXT_GAP_VERTICAL : TEXT_MARGIN_VERTICAL}
              $marginBottom={TEXT_MARGIN_VERTICAL}
              textStyle={TEXT_STYLE_SUBTITLE}
              $alignment={artworkShape === "circle" ? "center" : "left"}
              $width="50%"
            />
          ) : null}
        </>
      ) : (
        <ItemLink data={data}>
          <ItemArtwork
            artwork={data.artwork}
            artworkDisplayMode={data.artworkDisplayMode}
            height={artworkDimensions?.height}
            shape={artworkShape}
            showLockIcon={data.showLockIcon}
            progressBar={progressBar}
          />
          {showTitle ? (
            <ItemText
              $marginTop={TEXT_MARGIN_VERTICAL}
              $marginBottom={showSubtitle ? 0 : TEXT_MARGIN_VERTICAL}
              alignment={artworkShape === "circle" ? "center" : "left"}
              numberOfLines={titleNumberOfLines}
              textStyle={TEXT_STYLE_TITLE}
            >
              {data.title}
            </ItemText>
          ) : null}
          {showSubtitle ? (
            <ItemText
              $marginTop={showTitle ? TEXT_GAP_VERTICAL : TEXT_MARGIN_VERTICAL}
              $marginBottom={TEXT_MARGIN_VERTICAL}
              alignment={artworkShape === "circle" ? "center" : "left"}
              numberOfLines={subtitleNumberOfLines}
              textStyle={TEXT_STYLE_SUBTITLE}
              color="secondary"
            >
              {data.subtitle}
            </ItemText>
          ) : null}
        </ItemLink>
      )}
    </ItemContainer>
  )
}

export function getArtworkDisplayMode({
  tokens,
  shape,
  media,
}: {
  tokens: ResolvedTokens
  shape: ArtworkShape
  media: MediaImageFile
}): ArtworkDisplayMode {
  const aspectRatio = tokens.artworkListItem.shape[shape].aspectRatio
  // If the image has transparency then present it contained with no background
  // behind it.
  if (media.original.alpha) {
    return { mode: "containWithTransparentBackground" }
  }
  // If the image has a blurhash, is not being presented in a circle, and would
  // be cropped by more than 20% then present it contained with the blurhash
  // behind it.
  else if (
    shape !== "circle" &&
    media.original.blurhash &&
    media.original.width &&
    media.original.height &&
    Math.abs(1 - media.original.width / media.original.height / aspectRatio) >
      0.2
  ) {
    return {
      mode: "containWithBlurhash",
      blurhash: media.original.blurhash,
      width: media.original.width,
      height: media.original.height,
    }
  }
  // Present the image covering the entire artwork area by default
  else {
    return { mode: "cover" }
  }
}
